/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Define classroom list models at the module API boundary.
 * @state-owner none
 * @allowed-imports TypeScript standard library
 * @forbidden React; routes; API client
 * @public-api via modules/classrooms/index.ts
 * @side-effects none
 * @tests modules/classrooms/model/next-lesson.test.ts and module-contracts.test.ts
 */

export interface ClassroomSummary {
  id: string;
  name: string;
  subject: string;
  studentCount: number;
  joinCode: string;
  students: string[];
}

export interface ClassMemberSummary {
  id: string;
  userId: string;
  name: string;
  email: string | null;
  role: 'teacher' | 'co-teacher' | 'student';
  status: 'active' | 'invited' | 'pending' | 'removed' | 'archived';
  lastSeenAt: string | null;
}

export interface ClassResourceSummary {
  id: string;
  name: string;
  kind: 'document' | 'exam';
  mimeType: string | null;
  sizeBytes: number | null;
  url: string | null;
  status: 'ready' | 'uploading' | 'parsing' | 'error';
}

export interface ClassActivitySummary {
  id: string;
  title: string;
  kind: 'assignment' | 'question' | 'material' | 'quiz';
  status: 'draft' | 'published' | 'archived';
  dueDate: string | null;
  updatedAt: string | null;
  progressLabel?: string;
  scoreLabel?: string;
}

interface ExistingClassApiResponse {
  id: string;
  name: string;
  subject?: string;
  student_count?: number;
  join_code: string;
}

export interface ExistingClassMemberResponse {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  student_name?: string;
  role: ClassMemberSummary['role'];
  status: ClassMemberSummary['status'];
  last_seen_at?: string | null;
}

export function mapExistingClassResponse(value: ExistingClassApiResponse): ClassroomSummary {
  return {
    id: value.id,
    name: value.name,
    subject: value.subject ?? '未設定科目',
    studentCount: value.student_count ?? 0,
    joinCode: value.join_code,
    students: [],
  };
}

export function mapExistingClassMemberResponse(
  value: ExistingClassMemberResponse,
): ClassMemberSummary {
  return {
    id: value.id,
    userId: value.user_id,
    name: value.student_name ?? value.user_name ?? value.user_email ?? '學生',
    email: value.user_email ?? null,
    role: value.role,
    status: value.status,
    lastSeenAt: value.last_seen_at ?? null,
  };
}
